import React, { useEffect, useState } from "react";
import { DatePicker, Button } from "antd";
import styled from "styled-components";
import { PlusCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import { useMessage } from "../../../uteis/mensagens/useMessage";
import dayjs, { Dayjs } from "dayjs";

const Wrapper = styled.div`
  margin-top: 2rem;
`;

const Titulo = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 1rem;
  color: #222;
`;

const InputArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.2rem;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BotaoAdd = styled(Button)`
  display: flex;
  align-items: center;
`;

const Lista = styled.ul`
  list-style: none;
  padding-left: 0;
  margin-top: 1rem;
`;

const Item = styled.li`
  background: #f6faff;
  padding: 0.6rem 1rem;
  border-radius: 6px;
  margin-bottom: 0.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #333;
`;

const DeleteIcon = styled(DeleteOutlined)`
  cursor: pointer;
  color: #c00;
  margin-left: 1rem;

  &:hover {
    color: #a00;
  }
`;

// Tipagem dos períodos
type PeriodoObj = {
  inicio: Dayjs;
  fim: Dayjs;
};

// Datas de período não representam um instante no tempo. Ao interpretar um
// ISO com "Z" diretamente, o Dayjs aplica o fuso local e pode exibir o dia
// anterior (ex.: 20/07T00:00:00.000Z vira 19/07 no Brasil).
const parseDataPeriodo = (value: string): Dayjs => {
  const data = String(value || "");
  const dataISO = data.match(/^(\d{4}-\d{2}-\d{2})/);

  return dayjs(dataISO ? dataISO[1] : data);
};

// Props do componente
interface PeriodoProps {
  periodos: PeriodoObj[];
  interesseSelecionado?: any; // objeto vindo do backend, com propriedade 'periodos'
  setPeriodos: (periodos: PeriodoObj[]) => void;
  setBloquearBtn: (bloquear: boolean) => void;
}

export default function Periodo({
  periodos,
  interesseSelecionado,
  setPeriodos,
  setBloquearBtn,
}: PeriodoProps) {
  const [inicio, setInicio] = useState<Dayjs | null>(null);
  const [fim, setFim] = useState<Dayjs | null>(null);

  const exigePeriodo = false;
  const { showWarning, contextHolder } = useMessage();

  // Adicionar novo período
  const adicionarPeriodo = () => {
    if (!inicio || !fim) return;

    const novo: PeriodoObj = { inicio, fim };

    const hoje = dayjs().startOf("day");
    if (fim.isBefore(hoje)) {
      showWarning(
        "O período incluído já passou. Por favor, selecione datas futuras."
      );
      return;
    }
    if (fim.isSame(inicio, "day") || fim.isBefore(inicio, "day")) {
      showWarning("A data final deve ser posterior à data de início.");
      return;
    }

    const existe = periodos.some(
      (p) =>
        p.inicio.isSame(novo.inicio, "day") && p.fim.isSame(novo.fim, "day")
    );

    if (!existe) {
      setPeriodos([...periodos, novo]);
      setInicio(null);
      setFim(null);
    }
  };

  // Remover período
  const remover = (idx: number) => {
    setPeriodos(periodos.filter((_, i) => i !== idx));
  };

  // Scroll para o topo
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Bloquear ou desbloquear botão
  useEffect(() => {
    setBloquearBtn(periodos.length === 0);
  }, [periodos, setBloquearBtn]);

  // Preencher períodos do backend se existirem
  useEffect(() => {
    if (interesseSelecionado) {
      const periodosFormatados: PeriodoObj[] = [
        {
          inicio: parseDataPeriodo(interesseSelecionado.dataIni),
          fim: parseDataPeriodo(interesseSelecionado.dataFim),
        },
      ];
      setPeriodos(periodosFormatados);
    }
    // Se não houver interesseSelecionado, mantém vazio
  }, [interesseSelecionado, setPeriodos]);

  return (
    <Wrapper>
      {contextHolder}
      <Titulo>📅 Em quais períodos poderia ir? (pode ser mais de um)</Titulo>

      <InputArea>
        <Row>
          <DatePicker
            placeholder="Data de início"
            format="DD/MM/YYYY"
            value={inicio}
            onChange={(date) => {
              setInicio(date);
              setFim(null);
            }}
            style={{ width: "100%" }}
            required={exigePeriodo}
            getPopupContainer={() => document.body}
          />

          <DatePicker
            placeholder="Data de fim"
            format="DD/MM/YYYY"
            value={fim}
            onChange={(date) => setFim(date)}
            style={{ width: "100%" }}
            getPopupContainer={() => document.body}
            defaultPickerValue={inicio || undefined}
            disabledDate={(current) => {
              const hoje = dayjs().startOf("day");
              return !current
                ? false
                : current.isBefore(hoje, "day") ||
                    (inicio ? current.isBefore(inicio, "day") : false);
            }}
          />

          <BotaoAdd
            onClick={adicionarPeriodo}
            disabled={!inicio || !fim}
            type="primary"
          >
            <PlusCircleOutlined /> Adicionar
          </BotaoAdd>
        </Row>
      </InputArea>

      {periodos.length > 0 && (
        <Lista>
          {periodos.map((p, i) => (
            <Item key={i}>
              {p.inicio.format("DD/MM/YYYY")} até {p.fim.format("DD/MM/YYYY")}
              <DeleteIcon onClick={() => remover(i)} />
            </Item>
          ))}
        </Lista>
      )}
    </Wrapper>
  );
}
